import { useLingui } from '@lingui/react/macro';
import { useAtomValue } from 'jotai';

import type { ImageAnalyses } from '@/@types/latestImagesV2';
import { ImageSection } from '@/modules/claims/freeClaims/insuranceExport/styles';
import type { TToothTableData } from '@/modules/claims/freeClaims/insuranceExport/types';
import { showAnnotationsAtom } from '@/modules/claims/freeClaims/state';

import { SingleImageItem } from './SingleImageItem';
import { ToothLevelTreatmentsTable } from './ToothLevelTreatmentsTable';

type ImageThumbnailPairItemProps = {
  image: ImageAnalyses;
  findingTypes: string[];
  includedTeethIds: string[];
  tableData: TToothTableData[];
  filteredImageFindings: ImageAnalyses;
};

export const ImageThumbnailPairItem = ({
  image,
  findingTypes,
  includedTeethIds,
  tableData,
  filteredImageFindings,
}: ImageThumbnailPairItemProps) => {
  // hooks
  const { t } = useLingui();

  const showAnnotations = useAtomValue(showAnnotationsAtom);

  // Check if this is a panoramic image
  const isPanoramic = image.imageType === 'PANORAMIC';

  // show annotated image if there are any caries or restorative adjacent lucency findings
  const shouldShowAnnotatedImage =
    showAnnotations &&
    tableData.some(
      (item: TToothTableData) =>
        (item.caries && item.caries.length > 0) ||
        (item.restorativeAdjacentLucency &&
          item.restorativeAdjacentLucency.length > 0),
    );

  // show treatments table if there are any caries, dmf, or restorative adjacent lucency findings
  const shouldShowTreatmentsTable = tableData.some(
    (item: TToothTableData) =>
      (item.caries && item.caries.length > 0) ||
      (item.dmf && item.dmf > 0) ||
      (item.restorativeAdjacentLucency &&
        item.restorativeAdjacentLucency.length > 0),
  );

  return (
    <ImageSection
      className="vai-p-4"
      key={`image-section-${image.imageId}`}
      data-cy={`tooth-level-thumbnail-pair-${image.imageId}`}
    >
      {isPanoramic ? (
        // Panoramic layout: single wider image in the center
        <div className="vai-flex vai-justify-center vai-items-center vai-w-full">
          <SingleImageItem
            title={t`panoramic`}
            image={filteredImageFindings}
            height={500}
            width={1000}
          />
        </div>
      ) : (
        // Regular layout: side-by-side images
        <div className="vai-flex vai-justify-center vai-items-center vai-w-full vai-gap-8">
          <SingleImageItem title={t`original`} image={filteredImageFindings} />
          <div className={shouldShowAnnotatedImage ? '' : 'vai-invisible'}>
            <SingleImageItem
              title={t`annotated`}
              image={filteredImageFindings}
              enabledFindings={findingTypes}
              includedTeethIds={includedTeethIds}
            />
          </div>
        </div>
      )}
      {shouldShowTreatmentsTable && (
        <ToothLevelTreatmentsTable tableData={tableData} />
      )}
    </ImageSection>
  );
};
